import { useMemo, useState } from "react";
import { buildLuck, weeksInProgress } from "@averagejoesff/shared";
import type { LeagueHistory, LuckScope, PersonGame } from "@averagejoesff/shared";
import { LuckTable } from "./LuckTable";
import type { PeopleLookup } from "./people";

interface LuckViewProps {
  history: LeagueHistory;
  ledger: readonly PersonGame[];
  people: PeopleLookup;
  myId: string | null;
}

const ALL = "all";

/**
 * Who the schedule has been kind to, luckiest first. Defaults to the latest
 * season; "All seasons" adds every year up.
 */
export function LuckView({ history, ledger, people, myId }: LuckViewProps) {
  const seasons = useMemo(() => history.seasons.map((s) => s.season).sort((a, b) => b - a), [history]);
  const [scopeValue, setScopeValue] = useState<string>(String(seasons[0] ?? ALL));
  const scope: LuckScope = scopeValue === ALL ? ALL : Number(scopeValue);

  const luck = useMemo(() => buildLuck(ledger, scope, weeksInProgress(history)), [history, ledger, scope]);
  const live = scope !== ALL && history.seasons.find((s) => s.season === scope)?.isFinished === false;

  return (
    <div className="luck">
      <p className="home__line">
        Each week a score is also compared with everyone else's. <strong>Expected wins</strong> is how many games that earns;{" "}
        <strong>luck</strong> is wins won minus wins expected, so a positive number means the schedule helped.
      </p>

      <label className="season-picker luck__scope">
        <span className="season-picker__label">Period</span>
        <select className="season-picker__select" value={scopeValue} onChange={(e) => setScopeValue(e.target.value)}>
          <option value={ALL}>All seasons</option>
          {seasons.map((season) => (
            <option key={season} value={season}>
              {season}
            </option>
          ))}
        </select>
      </label>

      {luck.rows.length === 0 ? (
        <p className="home__line">No regular-season games have been played in {scopeValue === ALL ? "the league" : scopeValue} yet.</p>
      ) : (
        <>
          <p className="home__line">
            {luck.weeks} {luck.weeks === 1 ? "week" : "weeks"} of regular-season results{live ? " so far" : ""}.
          </p>
          <LuckTable rows={luck.rows} people={people} myId={myId} />
        </>
      )}
    </div>
  );
}
